import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Fade } from 'react-awesome-reveal'
import { useConfig } from '../hooks/useConfig'
import { Code, Terminal, Web, PhoneAndroid } from '@mui/icons-material'

export default function Header() {
    const { config } = useConfig()

    const title =
        config?.header.title || 'David LiaBraaten | Web & Mobile Developer'
    const [name, ...rest] = title.split('|')

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                p: { xs: 2, sm: 3 },
                width: '100%',
                backgroundColor: (theme) => theme.palette.text.primary,
                borderBottom: 1,
                borderColor: 'divider',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: (theme) =>
                        `linear-gradient(90deg, transparent, ${theme.palette.primary.main}40, transparent)`,
                    animation: 'scanLine 3s ease-in-out infinite',
                    '@keyframes scanLine': {
                        '0%, 100%': { opacity: 0.3 },
                        '50%': { opacity: 0.8 },
                    },
                },
            }}
        >
            {/* Subtle code brackets - professional touch */}
            <Code
                sx={{
                    position: 'absolute',
                    left: { xs: '5%', md: '10%' },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: { xs: 24, md: 32 },
                    color: 'primary.main',
                    opacity: 0.3,
                    display: { xs: 'none', sm: 'block' },
                }}
            />
            <Code
                sx={{
                    position: 'absolute',
                    right: { xs: '5%', md: '10%' },
                    top: '50%',
                    transform: 'translateY(-50%) rotate(180deg)',
                    fontSize: { xs: 24, md: 32 },
                    color: 'primary.main',
                    opacity: 0.3,
                    display: { xs: 'none', sm: 'block' },
                }}
            />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                        fontFamily: '"Space Grotesk", sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        color: (theme) => theme.palette.primary.contrastText,
                        textAlign: 'center',
                        letterSpacing: '-0.02em',
                        position: 'relative',
                        display: 'inline-block',
                        '&::before': {
                            content: '"<"',
                            position: 'absolute',
                            left: { xs: -20, sm: -28 },
                            color: 'primary.main',
                            opacity: 0.6,
                            fontSize: '0.8em',
                        },
                        '&::after': {
                            content: '"/>"',
                            position: 'absolute',
                            right: { xs: -20, sm: -28 },
                            color: 'primary.main',
                            opacity: 0.6,
                            fontSize: '0.8em',
                        },
                    }}
                >
                    {name.trim()}
                </Typography>
                {rest.length > 0 && (
                    <Fade delay={300} triggerOnce>
                        <Box
                            sx={{
                                mt: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            {/* Left side: Browser and Phone */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Web
                                    sx={{
                                        fontSize: { xs: 14, sm: 18 },
                                        color: 'secondary.main',
                                        opacity: 0.8,
                                        animation:
                                            'float 3s ease-in-out infinite',
                                        '@keyframes float': {
                                            '0%, 100%': {
                                                transform: 'translateY(0px)',
                                            },
                                            '50%': {
                                                transform: 'translateY(-3px)',
                                            },
                                        },
                                    }}
                                />
                                <PhoneAndroid
                                    sx={{
                                        fontSize: { xs: 14, sm: 18 },
                                        color: 'warning.main',
                                        opacity: 0.8,
                                        animation:
                                            'float 3s ease-in-out infinite 0.5s',
                                        '@keyframes float': {
                                            '0%, 100%': {
                                                transform: 'translateY(0px)',
                                            },
                                            '50%': {
                                                transform: 'translateY(-3px)',
                                            },
                                        },
                                    }}
                                />
                            </Box>

                            {/* Middle: Text */}
                            <Typography
                                variant="body1"
                                component="span"
                                sx={{
                                    fontSize: {
                                        xs: '0.85rem',
                                        sm: '1rem',
                                        md: '1.2rem',
                                    },
                                    fontWeight: 400,
                                    color: (theme) =>
                                        theme.palette.primary.contrastText,
                                    fontFamily: '"Noto Sans", monospace',
                                    letterSpacing: '0.05em',
                                    mx: 1,
                                }}
                            >
                                {rest.join('|').trim()}
                            </Typography>

                            {/* Right side: Terminal and Cursor */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Terminal
                                    sx={{
                                        fontSize: { xs: 14, sm: 18 },
                                        color: 'primary.main',
                                        opacity: 0.8,
                                    }}
                                />
                                <Box
                                    component="span"
                                    sx={{
                                        width: '2px',
                                        height: { xs: '14px', sm: '18px' },
                                        bgcolor: 'primary.main',
                                        animation: 'blink 1s step-end infinite',
                                        '@keyframes blink': {
                                            '0%, 50%': { opacity: 1 },
                                            '51%, 100%': { opacity: 0 },
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Fade>
                )}
            </Box>
        </Box>
    )
}
